import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { formatFieldValue } from "src/util/util.service";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Typography, type TableProps } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomButton,
  CustomModal,
  CustomSharedRangePicker,
  CustomTable,
  CustomTypography,
} from "sipli-fleet-core/components";
import { LOADER_ICON } from "src/assets/svgs";
import { useQueryGetCommandHistory } from "src/queries/commandHistory.queries";
import { useQueryGetConnectivityHeatmap } from "src/queries/connectivityHeatmap.queries";
import type { CommandHistoryRecord } from "src/services/commandHistoryService";
import { cn } from "src/util/cn";
import EpochDate from "../../../../../sipli-fleet-core/components/EpochDate/EpochDate";
import { staticConnectivityData } from "../../constant";
import { CommandSettingsModal, SendCommandModal } from "./CommandModals";
import ConnectivityHeatmap from "./ConnectivityHeatmap";
import MappingPage from "./Mapping/MappingPage";

interface DeviceHomeTabProps {
  unitId?: string;
  vehicleId?: string;
  modelId?: string | number;
}

const MAX_CONNECTIVITY_RANGE_DAYS = 7;
const MIN_CONNECTIVITY_DATE = dayjs("2017-01-01");
const USE_STATIC_CONNECTIVITY_DATA = false;

const commandStatusConfig: Record<number, { label: string; className: string }> = {
  1: {
    label: "Pending",
    className: "bg-orange-50 text-orange-500",
  },
  2: {
    label: "Delivered",
    className: "bg-status-driving-bg text-green1",
  },
  3: {
    label: "OK",
    className: "bg-status-driving-bg text-green1",
  },
  4: {
    label: "KO",
    className: "bg-red-soft text-red",
  },
};

const DeviceHomeTab = ({ unitId, vehicleId, modelId }: DeviceHomeTabProps) => {
  const { t, i18n } = useTranslation();
  const keepRangePickerOpenRef = useRef(false);
  const [pagination, setPagination] = useState({ page: 0, size: 10 });
  const [isRangePickerOpen, setIsRangePickerOpen] = useState(false);
  const [invalidDateModalOpen, setInvalidDateModalOpen] = useState(false);
  const [rangePickerResetKey, setRangePickerResetKey] = useState(0);
  const [connectivityDateRange, setConnectivityDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(6, "day"),
    dayjs(),
  ]);
  const [commandSettingsOpen, setCommandSettingsOpen] = useState(false);
  const [sendCommandOpen, setSendCommandOpen] = useState(false);
  const [liveCommandHistories, setLiveCommandHistories] = useState<CommandHistoryRecord[]>([]);

  const connectivityDatePresets = useMemo(
    () => [
      // {
      //   label: t("yesterday"),
      //   value: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
      // },
      // {
      //   label: t("today"),
      //   value: [dayjs(), dayjs()],
      // },
      {
        label: t("last7Days"),
        value: [dayjs().subtract(6, "day"), dayjs()],
      },
    ],
    [t]
  );

  const {
    data: connectivityData = [],
    isLoading: isConnectivityLoading,
    isFetching: isConnectivityFetching,
    isError: isConnectivityError,
    refetch: refetchConnectivity,
  } = useQueryGetConnectivityHeatmap(
    unitId
      ? {
          unitId,
          from: connectivityDateRange[0].format("YYYY-MM-DD"),
          to: connectivityDateRange[1].format("YYYY-MM-DD"),
        }
      : undefined,
    { enabled: !!unitId }
  );
  const connectivityGraphData = USE_STATIC_CONNECTIVITY_DATA
    ? staticConnectivityData
    : connectivityData;
  const isCommandHistoryEnabled =
    !!unitId && !isConnectivityLoading && !isConnectivityFetching;
  const {
    data: commandHistoryData,
    isLoading,
    isFetching,
    refetch,
  } = useQueryGetCommandHistory(
    unitId
      ? {
          unitId,
          page: pagination.page,
          size: pagination.size,
        }
      : undefined,
    { enabled: isCommandHistoryEnabled }
  );

  const response = commandHistoryData?.data?.response;
  const records = useMemo(() => {
    const apiRecords = response?.items ?? [];
    const liveRecordsById = new Map(
      liveCommandHistories.map((record) => [record.id, record]),
    );
    const mergedApiRecords = apiRecords.map((record) => ({
      ...record,
      ...liveRecordsById.get(record.id),
    }));

    if (pagination.page !== 0) return mergedApiRecords;

    const apiRecordIds = new Set(apiRecords.map((record) => record.id));
    const newLiveRecords = liveCommandHistories.filter(
      (record) => !apiRecordIds.has(record.id),
    );

    return [...newLiveRecords, ...mergedApiRecords];
  }, [liveCommandHistories, pagination.page, response?.items]);
  const totalCount = useMemo(() => {
    const apiRecords = response?.items ?? [];
    const apiRecordIds = new Set(apiRecords.map((record) => record.id));
    const newLiveRecordCount = liveCommandHistories.filter(
      (record) => !apiRecordIds.has(record.id),
    ).length;

    return (response?.totalItems ?? 0) + newLiveRecordCount;
  }, [liveCommandHistories, response?.items, response?.totalItems]);
  const handleCommandSent = useCallback((record: CommandHistoryRecord) => {
    setLiveCommandHistories((currentRecords) => [
      record,
      ...currentRecords.filter((currentRecord) => currentRecord.id !== record.id),
    ]);
    setPagination((currentPagination) => ({ ...currentPagination, page: 0 }));
  }, []);
  const handleCommandStatusChange = useCallback((
    historyId: number,
    status: CommandHistoryRecord["status"],
  ) => {
    setLiveCommandHistories((currentRecords) =>
      currentRecords.map((record) =>
        record.id === historyId ? { ...record, status } : record,
      ),
    );
  }, []);

  const columns: TableProps<CommandHistoryRecord>["columns"] = useMemo(
    () => [
      {
        title: t("command"),
        key: "command",
        width: 160,
        render: (_, record) => (
          <CustomTypography.CustomText className="!font-semibold">
            {record.command?.internalName || "--"}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("sent_at"),
        key: "sentAt",
        dataIndex: "createdDate",
        width: 170,
        render: (value) => (value ? <EpochDate date={value} appendFormat="HH:mm:ss" /> : "--"),
      },
      {
        title: t("status"),
        key: "status",
        width: 100,
        render: (_, record) => {
          const status = commandStatusConfig[record.status];

          return (
            <CustomTag
              className={cn(
                "min-w-[36px] justify-center",
                status?.className ?? "bg-gray1 text-gray9"
              )}
            >
              {status?.label ?? String(record.status)}
            </CustomTag>
          );
        },
      },
      {
        title: t("sender"),
        key: "sender",
        width: 160,
        render: (_, record) => record.updatedBy || "--",
      },
      {
        title: t("parameters"),
        key: "parameters",
        width: 430,
        render: (_, record) => {
          const params = Object.entries(record.params ?? {});

          if (params.length === 0) return "--";

          return (
            <div className="flex flex-col gap-1">
              {params.map(([key, value]) => (
                <div key={key} className="break-words">
                  {key}: {formatFieldValue(value, key, i18n.language)}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        title: t("response"),
        key: "response",
        width: 180,
        render: (_, record) => (
          <CustomTypography.CustomText className="!font-semibold">
            {record.response || record.reason || "--"}
          </CustomTypography.CustomText>
        ),
      },
    ],
    [t, i18n.language]
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="bg-white rounded-[16px] p-5">
        <div className="flex items-center justify-between gap-3">
          <CustomTypography.CustomText className="!text-[19.93px] !leading-[28px] !font-bold">
            {t("connectivity_graph")}
          </CustomTypography.CustomText>
          <div
            className={cn(
              "flex items-center justify-center h-[32px] w-[32px] rounded-[4px] border border-gray5 cursor-pointer select-none",
              {
                "cursor-not-allowed opacity-50": isConnectivityFetching,
              }
            )}
            onClick={() => {
              refetchConnectivity();
            }}
          >
            <img
              src={LOADER_ICON}
              alt={t("refresh")}
              className={cn({ "animate-spin": isConnectivityFetching })}
            />
          </div>
        </div>
        <div className="mt-1">
          <CustomTypography.CustomText className="!text-sm !leading-[18px] !text-gray9">
            {t("connectivity_graph_description")}
          </CustomTypography.CustomText>
        </div>
        <CustomSharedRangePicker
          key={rangePickerResetKey}
          value={connectivityDateRange}
          open={isRangePickerOpen}
          presets={connectivityDatePresets}
          disabledDate={(current: Dayjs) => {
            if (current.isBefore(MIN_CONNECTIVITY_DATE, "day") || current.isAfter(dayjs(), "day")) {
              return true;
            }

            return false;
          }}
          onChange={(dates: [Dayjs | null, Dayjs | null] | null) => {
            if (!dates?.[0] || !dates[1]) return;

            if (Math.abs(dates[1].diff(dates[0], "day")) >= MAX_CONNECTIVITY_RANGE_DAYS) {
              keepRangePickerOpenRef.current = true;
              setRangePickerResetKey((prev) => prev + 1);
              setIsRangePickerOpen(true);
              setInvalidDateModalOpen(true);
              return;
            }

            setConnectivityDateRange([dates[0], dates[1]]);
          }}
          onOpenChange={(open: boolean) => {
            if (keepRangePickerOpenRef.current && !open) {
              setIsRangePickerOpen(true);
              return;
            }

            setIsRangePickerOpen(open);
          }}
          allowClear={false}
          className="!mt-5 !h-[32px] !w-[256px] !rounded-[4px]"
        />
        <ConnectivityHeatmap
          data={connectivityGraphData}
          dateRange={connectivityDateRange}
          isLoading={isConnectivityLoading || isConnectivityFetching}
          isError={isConnectivityError}
        />
      </div>

      <div className="bg-white rounded-[16px] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <CustomTypography.CustomText className="!text-[19.93px] !leading-[28px] !font-bold">
            {t("commands")}
          </CustomTypography.CustomText>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <CustomButton
                type="secondary"
                buttonSize="medium"
                label={t("command_settings")}
                onClick={() => setCommandSettingsOpen(true)}
              />
              <CustomButton
                type="primary"
                buttonSize="medium"
                label={t("send_command")}
                className="!text-white"
                onClick={() => setSendCommandOpen(true)}
              />
            </div>
            <div
              className={cn(
                "flex items-center justify-center h-[32px] w-[32px] rounded-[4px] border border-gray5 cursor-pointer select-none",
                {
                  "cursor-not-allowed opacity-50": isFetching,
                }
              )}
              onClick={() => refetch()}
            >
              <img src={LOADER_ICON} alt="Loader" className={cn({ "animate-spin": isFetching })} />
            </div>
          </div>
        </div>

        <div className="overflow-hidden border border-primary6 rounded-[16px]">
          <CustomTable<CommandHistoryRecord>
            customClass="customer-command-history-table [&_.ant-table-thead>tr>th]:!py-3 [&_.ant-table-tbody>tr>td]:!py-3"
            columns={columns}
            data={records}
            rowKey={(record) => record.id}
            pageSize={pagination.size}
            currentPage={pagination.page}
            totalCount={totalCount}
            handlePagination={(page) => setPagination((prev) => ({ ...prev, page }))}
            handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
            showSearchInput={false}
            showCustomizeColumns={false}
            showDownload={false}
            isLoading={isLoading || isFetching}
            scroll={{ x: 1200 }}
            locale={{ emptyText: t("no_data") }}
          />
        </div>
      </div>

      <MappingPage unitId={unitId} vehicleId={vehicleId} modelId={modelId} />

      <CommandSettingsModal
        open={commandSettingsOpen}
        modelId={modelId}
        onClose={() => setCommandSettingsOpen(false)}
      />
      <SendCommandModal
        open={sendCommandOpen}
        modelId={modelId}
        vehicleId={vehicleId}
        onCommandSent={handleCommandSent}
        onCommandStatusChange={handleCommandStatusChange}
        onCommandComplete={() => refetch()}
        onClose={() => setSendCommandOpen(false)}
      />
      <CustomModal
        open={invalidDateModalOpen}
        footer={null}
        centered
        width={400}
        closable={false}
        keyboard={false}
        // maskClosable={false}
        zIndex={1200}
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => {
          keepRangePickerOpenRef.current = false;
          setInvalidDateModalOpen(false);
          setIsRangePickerOpen(true);
        }}
        onCancel={() => {
          keepRangePickerOpenRef.current = false;
          setInvalidDateModalOpen(false);
          setIsRangePickerOpen(true);
        }}
      >
        <div className="flex justify-center mb-[16px]">
          <ExclamationCircleFilled style={{ color: "#faad14" }} className="text-[48px]" />
        </div>

        <Typography.Title level={4} className="mb-[8px] text-center">
          {t("invalid_date_range")}
        </Typography.Title>

        <Typography className="text-[18px] text-center text-[rgba(0,0,0,0.45)]">
          {t("please_select_7_days")}
        </Typography>

        <div className="mt-[24px] text-center">
          <CustomButton
            label="OK"
            type="primary"
            onClick={() => {
              keepRangePickerOpenRef.current = false;
              setInvalidDateModalOpen(false);
              setIsRangePickerOpen(true);
            }}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default DeviceHomeTab;
